import { tv } from 'tailwind-variants';
import { DataTableShell } from './DataTableShell.js';

/** Three-state cell — no inventable `denied` / forbid policy (W7). */
export type MatrixCellState = 'granted' | 'limited' | 'unassigned';

const table = tv({
  base: 'min-w-full border-collapse text-sm',
});

const headerCell = tv({
  base: 'border-b border-[var(--color-rail-card-border)] bg-[var(--color-an-bg-elevated)] px-3 py-2 text-left font-medium text-[var(--color-an-text)]',
});

const cell = tv({
  base: 'border-b border-[var(--color-rail-card-border)] px-3 py-2 text-center',
});

const cellBadge = tv({
  base: 'inline-flex h-6 w-6 items-center justify-center rounded-full',
  variants: {
    state: {
      granted:
        'bg-[color-mix(in_oklab,var(--color-an-success)_15%,white)] text-[var(--color-an-success)]',
      limited:
        'bg-[color-mix(in_oklab,var(--color-an-warn)_15%,white)] text-[var(--color-an-warn)]',
      unassigned:
        'bg-[var(--color-an-bg-elevated)] text-[var(--color-an-text-disabled)]',
    },
  },
  defaultVariants: {
    state: 'unassigned',
  },
});

const CELL_GLYPH: Record<MatrixCellState, string> = {
  granted: '✓',
  limited: '−',
  unassigned: '—',
};

function resolveCellState(value: MatrixCellState | undefined): MatrixCellState {
  if (value === 'granted' || value === 'limited') {
    return value;
  }
  return 'unassigned';
}

export interface PermissionMatrixColumn {
  readonly id: string;
  readonly label: string;
}

export interface PermissionMatrixRow {
  readonly id: string;
  readonly label: string;
  readonly grants: Readonly<Record<string, MatrixCellState>>;
}

export interface PermissionMatrixProps {
  readonly columns: readonly PermissionMatrixColumn[];
  readonly rows: readonly PermissionMatrixRow[];
  readonly className?: string;
  readonly rowHeaderLabel?: string;
  readonly grantedAriaLabel?: string;
  readonly limitedAriaLabel?: string;
  readonly unassignedAriaLabel?: string;
  readonly ariaLabel?: string;
  readonly caption?: string;
}

export function PermissionMatrix({
  columns,
  rows,
  className,
  rowHeaderLabel = 'Role / Module',
  grantedAriaLabel = 'Granted',
  limitedAriaLabel = 'Limited',
  unassignedAriaLabel = 'Unassigned',
  ariaLabel,
  caption,
}: PermissionMatrixProps): JSX.Element {
  return (
    <DataTableShell className={className}>
      <div
        data-testid="permission-matrix"
        role="region"
        aria-label={ariaLabel}
      >
        <table className={table()}>
          {caption !== undefined && caption.length > 0 ? (
            <caption className="sr-only">{caption}</caption>
          ) : null}
          <thead>
            <tr>
              <th className={headerCell()}>{rowHeaderLabel}</th>
              {columns.map((column) => (
                <th key={column.id} className={headerCell()}>
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <th scope="row" className={`${headerCell()} text-left font-normal`}>
                  {row.label}
                </th>
                {columns.map((column) => {
                  const state = resolveCellState(row.grants[column.id]);
                  const ariaForState = ((): string => {
                    if (state === 'granted') {
                      return grantedAriaLabel;
                    }
                    if (state === 'limited') {
                      return limitedAriaLabel;
                    }
                    return unassignedAriaLabel;
                  })();
                  return (
                    <td key={column.id} className={cell()}>
                      <span
                        className={cellBadge({ state })}
                        aria-label={ariaForState}
                        data-state={state}
                      >
                        {CELL_GLYPH[state]}
                      </span>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </DataTableShell>
  );
}
